import { ref, type Ref, onMounted, onBeforeUnmount, watch, type ComputedRef } from 'vue'

export default function useSectionNavigation(
  contentRef: Ref<{ $el: HTMLElement } | null>,
  navigationItems: ComputedRef<{ id: string; sectionId?: string; index?: number }[]>,
  activeNavigationItems: Ref<{ id: string; sectionId?: string; previewMode?: boolean }[]>,
  isEditMode: Ref<boolean>,
) {
  const isSSR = typeof window === 'undefined'
  const isScrollEndSupported = !isSSR && 'onscrollend' in window
  const SCROLL_SELECTOR = '.n-layout-scroll-container'
  const isScrolling = ref(false)
  const scrollUsed = ref(false)
  const activeIndex = ref(0)
  let scrollableContainer: HTMLElement | null = null
  let scrollTimeout: ReturnType<typeof setTimeout> | null = null

  const setActiveIndex = (index: number) => {
    activeIndex.value = isFinite(index) ? index : 0
  }

  const getVisibleSectionRange = () => {
    let start = Infinity
    let end = -Infinity

    const itemsInActiveMode = getNavigationItemsInActiveMode()

    for (const { id, sectionId } of itemsInActiveMode) {
      const index = navigationItems.value?.findIndex(
        (component) => component.id === id && component.sectionId === sectionId,
      )

      if (index < start) {
        start = index
      }

      if (index > end) {
        end = index
      }
    }

    return { start, end }
  }

  const isOutsideOfVisibleRange = (start: number, end: number) => activeIndex.value < start || activeIndex.value > end

  const scrollTo = (id: string, sectionId?: string, sectionIndex: number = 0, scrollOffset: number = 0) => {
    let element: Element | null = null

    const componentSelector = `[data-component-id="${id}"]`
    const sectionSelector = sectionId ? `[data-section-id="${sectionId}"]` : ''

    if (!contentRef.value?.$el) {
      console.error('Content reference not found')
      return
    }

    try {
      // Get all instances of this component
      const components = Array.from(contentRef.value.$el.querySelectorAll(componentSelector))

      if (components.length === 0) {
        console.error(`No components found with ID ${id}`)
        return
      }

      // For section lists, we need to handle both the component and its sections
      if (sectionId) {
        // Get all sections with this ID across all components
        const allSections: Element[] = []

        // Gather all sections with the specified ID
        components.forEach((component) => {
          const sectionsInComponent = Array.from(component.querySelectorAll(sectionSelector))
          allSections.push(...sectionsInComponent)
        })

        if (allSections.length === 0) {
          console.error(`No sections found with ID ${sectionId}`)
          return
        }

        // If we have multiple sections with the same ID, use the sectionIndex parameter
        // to determine which specific section to scroll to
        if (allSections.length > 1 && sectionIndex < allSections.length) {
          element = allSections[sectionIndex]
        } else if (allSections.length === 1) {
          // If there's only one section with this ID, just use it
          element = allSections[0]
        } else {
          // Fallback to the first section if we can't determine which one to use
          element = allSections[0]
        }
      } else {
        // For non-section components, use the component itself
        if (sectionIndex < components.length) {
          element = components[sectionIndex]
        } else {
          element = components[0]
        }
      }
    } catch (error) {
      console.error('Error finding element:', error)
      return
    }

    if (!element) {
      console.error(`Could not find element to scroll to`)
      return
    }

    if (!scrollableContainer) {
      console.error('No scrollable container found')
      return
    }

    const currentScrollTop = scrollableContainer.scrollTop
    const { top } = element.getBoundingClientRect()
    const elementTop = top + currentScrollTop
    const safetyOffset = 5
    let topBarHeight = 0
    let layoutPadding = 0

    if (isEditMode.value && !isSSR) {
      const element = document.getElementById('slotty-ui-header') ?? document.querySelector('.p-app-bar')
      topBarHeight = element ? parseInt(window.getComputedStyle(element).getPropertyValue('height')) : 0
    }

    if (!isSSR) {
      const element = contentRef.value?.$el
      layoutPadding = element ? parseInt(window.getComputedStyle(element).getPropertyValue('padding-top')) : 0
    }

    const scrollPosition = Math.round(elementTop) - topBarHeight - layoutPadding + safetyOffset + scrollOffset

    scrollableContainer.scrollTo({
      top: scrollPosition,
      // behavior: 'smooth', // TODO: enabling it causes v-element-visibility to not work correctly
    })
  }

  const navigateTo = (index: number | null, sectionIndex: number = 0, scrollOffset: number = 0) => {
    if (index === null) return

    const item = navigationItems.value?.[index]

    if (!item) {
      console.warn(`No navigation item found at index ${index}`)
      return
    }

    try {
      // For sections with duplicate IDs, use the sectionIndex parameter for navigation
      const occurrenceIndex = item.sectionId ? sectionIndex : index

      scrollTo(item.id, item.sectionId, occurrenceIndex, scrollOffset)
      activeIndex.value = index
    } catch (error) {
      console.error('Navigation failed:', error)
    }
  }

  const onScrollStart = () => {
    scrollUsed.value = true
    isScrolling.value = true

    if (!isScrollEndSupported) {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout)
      }

      scrollTimeout = setTimeout(() => {
        isScrolling.value = false
      }, 200)
    }
  }

  const onScrollEnd = () => {
    isScrolling.value = false
  }

  const getNavigationItemsInActiveMode = () =>
    activeNavigationItems.value.filter(
      (item) => item.previewMode === undefined || item.previewMode === !isEditMode.value,
    )

  const manageActiveIndex = () => {
    watch(
      [activeNavigationItems, isScrolling],
      () => {
        if (!isEditMode.value) return // ignore in presentation mode
        if (isScrolling.value || !scrollUsed.value) {
          return
        }

        const { start, end } = getVisibleSectionRange()

        if (isOutsideOfVisibleRange(start, end)) {
          setActiveIndex(start)
        }
      },
      { deep: true },
    )
  }

  onMounted(() => {
    scrollableContainer = contentRef.value?.$el.querySelector(SCROLL_SELECTOR) ?? null
    scrollableContainer?.addEventListener('scroll', onScrollStart)
    scrollableContainer?.addEventListener('scrollend', onScrollEnd)
    manageActiveIndex()
  })

  onBeforeUnmount(() => {
    scrollableContainer?.removeEventListener('scroll', onScrollStart)
    scrollableContainer?.removeEventListener('scrollend', onScrollEnd)
  })

  return {
    scrollTo,
    navigateTo,
    activeIndex,
  }
}
